import React from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import { addAdmin } from "../../utils/addAdmin";

interface DistrictOption {
  id: string;
  name: string;
}

interface AddAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  districtId?: number | string;
  districtName?: string;
  districts?: DistrictOption[];
  title?: string;
  onSuccess?: () => void;
}

const validationSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  districtId: yup.string().when("districts", {
    is: (districts: any) => Array.isArray(districts) && districts.length > 0,
    then: (schema) => schema.required("District is required"),
    otherwise: (schema) => schema,
  }),
});

export default function AddAdminModal({
  isOpen,
  onClose,
  districtId,
  districtName,
  districts = [],
  title,
  onSuccess,
}: AddAdminModalProps) {
  const initialDistrictId =
    districtId || (districts.length > 0 ? districts[0].id : "");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        title ||
        (districtName ? `Add New Admin to ${districtName}` : "Add New Admin")
      }
    >
      <Formik
        initialValues={{
          email: "",
          password: "",
          firstName: "",
          lastName: "",
          districtId: initialDistrictId,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const fullName = `${values.firstName} ${values.lastName}`.trim();
          const usedDistrictId = districtId || values.districtId;
          if (!usedDistrictId) {
            toast.error("District is required");
            setSubmitting(false);
            return;
          }
          const districtIdNumber =
            typeof usedDistrictId === "string"
              ? parseInt(usedDistrictId, 10)
              : usedDistrictId;
          const { error } = await addAdmin({
            email: values.email,
            password: values.password,
            firstName: values.firstName,
            lastName: values.lastName,
            fullName,
            district_id: districtIdNumber,
          });
          if (error) {
            toast.error(error);
            setSubmitting(false);
            return;
          }
          toast.success("Admin successfully added!");
          resetForm();
          onClose();
          onSuccess?.();
        }}
        enableReinitialize
      >
        {(formik) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Field
                name="email"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.email && formik.touched.email && (
                <div className="text-red-500 text-sm">
                  {formik.errors.email}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Field
                name="password"
                type="password"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.password && formik.touched.password && (
                <div className="text-red-500 text-sm">
                  {formik.errors.password}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <Field
                name="firstName"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.firstName && formik.touched.firstName && (
                <div className="text-red-500 text-sm">
                  {formik.errors.firstName}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <Field
                name="lastName"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.lastName && formik.touched.lastName && (
                <div className="text-red-500 text-sm">
                  {formik.errors.lastName}
                </div>
              )}
            </div>
            {districts.length > 0 && !districtId && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  District
                </label>
                <Field
                  as="select"
                  name="districtId"
                  className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Field>
                {formik.errors.districtId && formik.touched.districtId && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.districtId}
                  </div>
                )}
              </div>
            )}
            <div className="pt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-small btn-secondary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-small ${
                  formik.isSubmitting ? "btn-disabled" : ""
                }`}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting ? "Adding..." : "Add Admin"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
